import * as localStorageKeys from "./constants/localStorageKeys";

import { AccessItem, Location, User } from "./models/Models";
import moment, { Moment } from "moment";

import { ILanguage } from "./context/LangContext";
import { ScreeningStatus } from "./models/getCancerScreeningsModel";
import { borderControlStatus } from "./constants/enums";
import translations from "./assets/json/translations.json";

export const hasUserAccess = (id: number, user: User) => {
  const item = user?.accessList?.find((item: AccessItem) => {
    return item.id === id;
  });
  return item !== undefined;
};

export function sleep(time: number = 1000) {
  return new Promise((resolve) => setTimeout(resolve, time));
}

export function parseTemplateString(str: string, ...args: string[]) {
  args.forEach((arg) => (str = str.replace(/\$\{.*?\}/, arg)));
  return str.replace(/\$\{.*?\}/g, "");
}

export function getLocationLabel(locationKey: string, locations: Location[]) {
  const result = locations.find((l) => l.key === locationKey);
  if (result) {
    return result.label;
  }
  return null;
}

export function showResidenceInfo(testType: string) {
  return (
    !!testType &&
    testType !== borderControlStatus.REGISTERED_FOR_SYMPTOM_SAMPLING.toString()
  );
}

export function capitalize(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function compareSSN(a: string, b: string) {
  const pattern = new RegExp(/^[0-9]{10}$/);
  if (!pattern.test(a)) {
    return -1;
  }
  if (!pattern.test(b)) {
    return 1;
  }

  const centuryA = !parseInt(a[9]) ? 21 : 20;
  const centuryB = !parseInt(b[9]) ? 21 : 20;
  if (centuryA !== centuryB) {
    return centuryA > centuryB ? -1 : 1;
  }

  const yearA = parseInt(a.substr(4, 2));
  const yearB = parseInt(b.substr(4, 2));
  if (yearA !== yearB) {
    return yearA > yearB ? -1 : 1;
  }

  const monthA = parseInt(a.substr(2, 2));
  const monthB = parseInt(b.substr(2, 2));
  if (monthA !== monthB) {
    return monthA > monthB ? -1 : 1;
  }

  const dayA = parseInt(a.substr(0, 2));
  const dayB = parseInt(b.substr(0, 2));
  return dayA === dayB ? 0 : dayA > dayB ? -1 : 1;
}

export function compareFlowDate(a: any, b: any) {
  if (!a.created) {
    return -1;
  }
  if (!b.created) {
    return 1;
  }
  return moment(a.created).isAfter(b.created) ? 1 : -1;
}

export function isAccessible(id: number, user: any) {
  const item = user?.accessList?.find((item: AccessItem) => {
    return item.id === id;
  });
  return item !== undefined;
}

export function getBirthYearBySSN(ssn: string) {
  const pattern = new RegExp(/^[0-9]{10}$/);
  if (!pattern.test(ssn)) {
    return 0;
  }

  const century = !parseInt(ssn[9]) ? 2000 : 1900;
  const year = parseInt(ssn.substr(4, 2));
  return century + year;
}

export function toISOStringTimezone(date: Moment) {
  const isoDate = date.toISOString(true);
  if (isoDate.includes("+")) {
    return isoDate.split("+")[0];
  }
  return isoDate;
}

export function getYesNoOptions(lang: ILanguage) {
  return [
    {
      label: translations.yes[lang],
      value: "true",
    },
    {
      label: translations.no[lang],
      value: "false",
    },
  ];
}

export function getYesNoNeitherOptions(lang: ILanguage) {
  return [
    {
      label: translations.notSelected[lang],
      value: "neither",
    },
    {
      label: translations.yes[lang],
      value: "true",
    },
    {
      label: translations.no[lang],
      value: "false",
    },
  ];
}

export function getFileTypeText(file: string) {
  const splitString = file.split("/");
  if (splitString.length > 1) {
    return splitString[1];
  }

  return file;
}

export function splitFullName(fullName: string) {
  if (fullName && fullName.length > 0) {
    var names = fullName.split(" ");

    var lastName = names.pop();
    var firstName = "";
    if (lastName) {
      firstName = fullName.replace(lastName, "").trim();
    }
    return { firstName: firstName, lastName: lastName };
  } else {
    return null;
  }
}
export function getCenturyFromSsn(ssn: string) {
  const lastDigit = ssn.slice(9, 10);
  const isBornAfter2000 = lastDigit === "0";
  return isBornAfter2000 ? "20" : "19";
}
export function getDateOfBirthFromSsn(ssn: string) {
  const century = getCenturyFromSsn(ssn);
  return moment(
    `${ssn.slice(0, 2)}.${ssn.slice(2, 4)}.${century}${ssn.slice(4, 6)}`,
    "DD.MM.YYYY"
  );
}

export function formatSsn(ssn: string | undefined) {
  return ssn?.replace(/(\d{6})(\d{4})/, "$1-$2");
}

export function getAgeInYearsFromSsnAtDate(ssn: string | undefined, date: Date | undefined, lang: ILanguage) {
  if(ssn !== undefined && date !== undefined)
  {
    const birthDate = getDateOfBirthFromSsn(ssn);
    
    const ageInYears = moment(date).diff(birthDate, 'years');
    
    return ageInYears % 10 === 1 ? 
    ageInYears + " " + translations.yearAgeText[lang] :
    ageInYears + " " + translations.yearsAgeText[lang]
  }
  else
  {
    return translations.notComputable[lang];
  }
  
}

export function clearSavedNewScreeningRequest() {
  localStorage.removeItem(localStorageKeys.NEW_SCREENING_REQUESTS_FILTER_FORM);
  localStorage.removeItem(localStorageKeys.NEW_SCREENING_REQUESTS_PAGE_SIZE);
  localStorage.removeItem(
    localStorageKeys.NEW_SCREENING_REQUESTS_SORT_DIRECTION
  );
  localStorage.removeItem(localStorageKeys.NEW_SCREENING_REQUESTS_SORT_KEY);
}

export function clearSavedSentScreeningRequest() {
  localStorage.removeItem(localStorageKeys.SENT_SCREENING_REQUESTS_FILTER_FORM);
  localStorage.removeItem(localStorageKeys.SENT_SCREENING_REQUESTS_PAGE_SIZE);
  localStorage.removeItem(
    localStorageKeys.SENT_SCREENING_REQUESTS_SORT_DIRECTION
  );
  localStorage.removeItem(localStorageKeys.SENT_SCREENING_REQUESTS_SORT_KEY);
}

export function clearLocalStorage() {
  clearSavedNewScreeningRequest();
  clearSavedSentScreeningRequest();
}

export function getScreeningStatusText(screeningStatus: number, lang: ILanguage): string {
  switch (screeningStatus) {
    case ScreeningStatus.Created:
      return translations.new[lang];
    case ScreeningStatus.RequestsSent:
      return translations.requestsSent[lang];
    case ScreeningStatus.SomeResultsArrived:
      return translations.someResultsArrived[lang];
    case ScreeningStatus.AllResultsArrived:
      return translations.allResultsArrived[lang];
    case ScreeningStatus.Completed:
        return translations.confirmed[lang];
    case ScreeningStatus.Canceled:
      return translations.deleted[lang];
    case ScreeningStatus.SentToSkrin:
      return translations.sentToSkrin[lang];
    default:
      return translations.unknown[lang];
  }
}
